/**
 * BillingPage — firm-facing plan management (021 follow-up, Cursor-inspired).
 *
 * Three states:
 *  - no plan: seat summary + Start plan (Stripe Checkout)
 *  - active (monthly): annual-upgrade banner + Payment / Invoices / Cancel
 *  - cancel pending: amber banner + Resume instead of Cancel
 * Admin-only: members who deep-link here bounce to /workspace/members.
 * Old v1 mailbox-credits
 * surface is retired (recoverable at git history).
 */

import { useQuery as useReactQuery } from "@tanstack/react-query";
import { useAction, useQuery } from "convex/react";
import { ConvexError } from "convex/values";
import { ChevronDownIcon, ExternalLinkIcon, GiftIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { Navigate, useSearchParams } from "react-router";
import { toast } from "sonner";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { api } from "../../../convex/_generated/api";

function dollars(cents: number): string {
	return `$${(cents / 100).toLocaleString(undefined, {
		minimumFractionDigits: cents % 100 === 0 ? 0 : 2,
	})}`;
}

function formatDate(ms: number): string {
	return new Date(ms).toLocaleDateString(undefined, {
		year: "numeric",
		month: "long",
		day: "numeric",
	});
}

function monthKey(ms: number): string {
	return new Date(ms).toLocaleDateString(undefined, {
		year: "numeric",
		month: "long",
	});
}

function errorMessage(err: unknown): string {
	if (err instanceof ConvexError) {
		const data = err.data as { message?: string } | undefined;
		if (data?.message) return data.message;
	}
	return "Something went wrong. Please try again.";
}

type Invoice = {
	id: string;
	createdMs: number;
	status: string;
	totalCents: number;
	hostedInvoiceUrl: string | null;
};

function InvoicesCard() {
	const listInvoices = useAction(api.billing.teamPage.listInvoices);
	const { data: invoices = null } = useReactQuery<Invoice[]>({
		queryKey: ["billing-invoices"],
		queryFn: async () => {
			try {
				return await listInvoices({});
			} catch {
				return [];
			}
		},
		retry: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
	});
	const [month, setMonth] = useState<string | null>(null);

	const months = [
		...new Set((invoices ?? []).map((i) => monthKey(i.createdMs))),
	];
	const shown = (invoices ?? []).filter(
		(i) => month === null || monthKey(i.createdMs) === month,
	);

	return (
		<Card>
			<CardHeader>
				<CardTitle>Invoices</CardTitle>
				{months.length > 0 && (
					<CardAction>
						<DropdownMenu>
							<DropdownMenuTrigger
								render={<Button variant="outline" size="sm" />}
							>
								{month ?? "All months"}
								<ChevronDownIcon className="size-3.5" />
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								<DropdownMenuItem onClick={() => setMonth(null)}>
									All months
								</DropdownMenuItem>
								{months.map((m) => (
									<DropdownMenuItem key={m} onClick={() => setMonth(m)}>
										{m}
									</DropdownMenuItem>
								))}
							</DropdownMenuContent>
						</DropdownMenu>
					</CardAction>
				)}
			</CardHeader>
			<CardContent>
				{invoices === null ? (
					<Skeleton className="h-16 w-full" />
				) : shown.length === 0 ? (
					<p className="text-sm text-muted-foreground">No invoices yet.</p>
				) : (
					<Table>
						<TableHeader>
							<TableRow>
								<TableHead>Date</TableHead>
								<TableHead>Status</TableHead>
								<TableHead>Amount</TableHead>
								<TableHead className="text-right">Invoice</TableHead>
							</TableRow>
						</TableHeader>
						<TableBody>
							{shown.map((inv) => (
								<TableRow key={inv.id}>
									<TableCell>{formatDate(inv.createdMs)}</TableCell>
									<TableCell className="capitalize">{inv.status}</TableCell>
									<TableCell>{dollars(inv.totalCents)}</TableCell>
									<TableCell className="text-right">
										{inv.hostedInvoiceUrl && (
											<a
												href={inv.hostedInvoiceUrl}
												target="_blank"
												rel="noreferrer"
												className="inline-flex items-center gap-1 text-sm underline underline-offset-2"
											>
												View <ExternalLinkIcon className="size-3.5" />
											</a>
										)}
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				)}
			</CardContent>
		</Card>
	);
}

export default function BillingPage() {
	const data = useQuery(api.billing.teamPage.getBillingPage);
	const startCheckout = useAction(api.billing.teamPage.startCheckout);
	const createPortalSession = useAction(
		api.billing.teamPage.createPortalSession,
	);
	const switchToAnnual = useAction(api.billing.teamPage.switchToAnnual);
	const setCancelAtPeriodEnd = useAction(
		api.billing.teamPage.setCancelAtPeriodEnd,
	);
	const previewStartPlan = useAction(api.billing.teamPage.previewStartPlan);
	const [busy, setBusy] = useState(false);
	const [annualOpen, setAnnualOpen] = useState(false);
	const [cancelOpen, setCancelOpen] = useState(false);
	const [searchParams, setSearchParams] = useSearchParams();

	const needsStartPreview =
		data !== undefined &&
		!data.hasTeamSubscription &&
		data.viewerRole === "admin";
	const startPreviewQuery = useReactQuery({
		queryKey: ["billing-start-plan-preview"],
		queryFn: () => previewStartPlan({}),
		enabled: needsStartPreview,
		retry: false,
		refetchOnReconnect: false,
		refetchOnWindowFocus: false,
	});
	const startPreview = needsStartPreview
		? (startPreviewQuery.data ?? null)
		: null;

	useState(() => {
		const outcome = searchParams.get("billing");
		if (!outcome) return;
		if (outcome === "success") {
			toast.success("Plan is active — welcome aboard");
		} else if (outcome === "cancelled") {
			toast("Checkout cancelled — no changes made");
		}
		searchParams.delete("billing");
		setSearchParams(searchParams, { replace: true });
	});

	const pageRootRef = useCallback((node: HTMLDivElement | null) => {
		if (node === null) return;
		function onPageShow(event: PageTransitionEvent) {
			if (event.persisted) setBusy(false);
		}
		window.addEventListener("pageshow", onPageShow);
		return () => window.removeEventListener("pageshow", onPageShow);
	}, []);

	async function run(fn: () => Promise<unknown>, doneMsg?: string) {
		setBusy(true);
		try {
			await fn();
			if (doneMsg) toast.success(doneMsg);
		} catch (err) {
			toast.error(errorMessage(err));
		} finally {
			setBusy(false);
		}
	}

	async function redirect(fn: () => Promise<{ url: string }>) {
		setBusy(true);
		try {
			const { url } = await fn();
			window.location.href = url;
		} catch (err) {
			toast.error(errorMessage(err));
			setBusy(false);
		}
	}

	const isAdmin = data?.viewerRole === "admin";
	// Billing is admin-only; members who deep-link here bounce to the team list.
	if (data && !isAdmin) {
		return <Navigate to="/workspace/members" replace />;
	}
	const seatSummary = data
		? [
				data.seatCounts.standard > 0 &&
					`${data.seatCounts.standard} ${data.seatPlans.standard.label} × ${dollars(data.seatPlans.standard.monthlyCents)}`,
				data.seatCounts.premium > 0 &&
					`${data.seatCounts.premium} ${data.seatPlans.premium.label} × ${dollars(data.seatPlans.premium.monthlyCents)}`,
			]
				.filter(Boolean)
				.join(" + ")
		: "";

	return (
		<div ref={pageRootRef} className="flex h-full flex-col">
			<header className="flex shrink-0 items-center border-b py-3 pl-14 pr-6">
				<h1 className="text-lg font-semibold">Billing</h1>
			</header>
			<ScrollArea className="min-h-0 flex-1">
				<div className="mx-auto flex w-full max-w-3xl flex-col gap-5 p-6">
					{data === undefined ? (
						<>
							<Skeleton className="h-16 w-full" />
							<Skeleton className="h-32 w-full" />
						</>
					) : !data.hasTeamSubscription ? (
						<Card>
							<CardContent className="p-6">
								<h2 className="text-xl font-semibold">Start your plan</h2>
								<p className="mt-2 text-muted-foreground">
									{seatSummary
										? `${seatSummary} = ${dollars(data.totalCents)}/mo`
										: "Add members first, then start your plan."}
								</p>
								{startPreview &&
									(startPreview.onboardingCents > 0 ||
										startPreview.creditAppliedCents > 0) && (
										<div className="mt-2 text-sm text-muted-foreground">
											{startPreview.onboardingCents > 0 && (
												<p>
													Onboarding (one-time):{" "}
													{dollars(startPreview.onboardingCents)}
												</p>
											)}
											<p>
												Due today: {dollars(startPreview.dueTodayCents)}
												{startPreview.creditAppliedCents > 0
													? ` — ${dollars(
															startPreview.creditAppliedCents,
														)} account credit applied`
													: ""}
												.
											</p>
										</div>
									)}
								{isAdmin ? (
									<Button
										className="mt-5"
										disabled={busy || data.totalCents === 0}
										onClick={() => void redirect(() => startCheckout({}))}
									>
										Start plan
									</Button>
								) : (
									<p className="mt-5 text-sm text-muted-foreground">
										Your firm admins manage billing.
									</p>
								)}
							</CardContent>
						</Card>
					) : (
						<>
							{data.cancelAtPeriodEnd ? (
								<div className="flex items-center justify-between rounded-lg border border-warning/40 bg-warning/10 px-4 py-3">
									<span className="text-sm">
										Your plan ends{" "}
										{data.currentPeriodEnd
											? formatDate(data.currentPeriodEnd)
											: "at period end"}
										. You'll keep access until then.
									</span>
									{isAdmin && (
										<Button
											variant="outline"
											size="sm"
											disabled={busy}
											onClick={() =>
												void run(
													() => setCancelAtPeriodEnd({ cancel: false }),
													"Plan resumed",
												)
											}
										>
											Resume
										</Button>
									)}
								</div>
							) : data.interval === "month" && isAdmin ? (
								<div className="flex items-center justify-between rounded-lg border border-primary/20 bg-primary/5 px-4 py-3">
									<span className="flex items-center gap-2 text-sm">
										<GiftIcon className="size-4" />
										Switch to annual billing and save 17%
									</span>
									<Button size="sm" onClick={() => setAnnualOpen(true)}>
										Upgrade now
									</Button>
								</div>
							) : null}

							{data.currentPeriodEnd && !data.cancelAtPeriodEnd && (
								<div className="text-sm text-muted-foreground">
									Team plan renews {formatDate(data.currentPeriodEnd)}
								</div>
							)}

							{isAdmin && (
								<Card>
									<CardHeader>
										<CardTitle>Payment</CardTitle>
										<CardDescription>
											Update your payment details
										</CardDescription>
										<CardAction>
											<Button
												variant="outline"
												disabled={busy}
												onClick={() =>
													void redirect(() => createPortalSession({}))
												}
											>
												Manage in Stripe
											</Button>
										</CardAction>
									</CardHeader>
								</Card>
							)}

							{isAdmin && <InvoicesCard />}

							{isAdmin && !data.cancelAtPeriodEnd && (
								<Card>
									<CardHeader>
										<CardTitle>Cancel</CardTitle>
										<CardDescription>
											Your plan stays active until the end of the billing
											period.
										</CardDescription>
										<CardAction>
											<Button
												variant="outline"
												disabled={busy}
												onClick={() => setCancelOpen(true)}
											>
												Cancel
											</Button>
										</CardAction>
									</CardHeader>
								</Card>
							)}

							{!isAdmin && (
								<p className="text-sm text-muted-foreground">
									Your firm admins manage billing.
								</p>
							)}
						</>
					)}
				</div>
			</ScrollArea>

			<AlertDialog open={annualOpen} onOpenChange={setAnnualOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Upgrade to yearly billing</AlertDialogTitle>
						<AlertDialogDescription>
							{data
								? `Seats bill at the annual rate — ${dollars(data.seatPlans.standard.annualMonthlyCents)}/mo ${data.seatPlans.standard.label} and ${dollars(data.seatPlans.premium.annualMonthlyCents)}/mo ${data.seatPlans.premium.label}, instead of ${dollars(data.seatPlans.standard.monthlyCents)} and ${dollars(data.seatPlans.premium.monthlyCents)}. `
								: ""}
							The yearly subscription is charged immediately, prorated against
							the unused time on your current month.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Cancel</AlertDialogCancel>
						<AlertDialogAction
							onClick={() => {
								setAnnualOpen(false);
								void run(
									() => switchToAnnual({}),
									"Switched to annual billing",
								);
							}}
						>
							Upgrade
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Cancel your plan?</AlertDialogTitle>
						<AlertDialogDescription>
							Your firm keeps access until{" "}
							{data?.currentPeriodEnd
								? formatDate(data.currentPeriodEnd)
								: "the end of the billing period"}
							. No refunds are issued for the remaining time, and you can resume
							anytime before then.
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>Keep plan</AlertDialogCancel>
						<AlertDialogAction
							variant="destructive"
							onClick={() => {
								setCancelOpen(false);
								void run(
									() => setCancelAtPeriodEnd({ cancel: true }),
									"Plan will end at the period close",
								);
							}}
						>
							Cancel plan
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</div>
	);
}
